<template>
  <BaseVeeForm :action="submit" ensure-error-keys-camel-case v-slot="formProps" class="space-y-8">
    <VModal ref="modal" @modal:hidden="$emit('hide')" disable-backdrop-click>
      <div class="mx-auto bg-white rounded-lg text-left shadow-xl max-w-lg w-full">
        <div class="flex flex-col">
          <div class="flex-none px-4 py-4 sm:px-6 border-b border-gray-200">
            <div class="flex justify-between items-center">
              <div class="flex-1">Add Member</div>
              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="flex-1 px-4 pt-4 pb-4 sm:pb-6 sm:px-6">
            <div class="space-y-8">
              <div class="space-y-4">
                <BaseFormCheckbox v-model="addFromExistingUser">
                  Add from existing user
                </BaseFormCheckbox>
                <ValidationProvider v-if="addFromExistingUser" name="user" slim vid="user">
                  <template #default="{ errors }">
                    <BaseFormGroup>
                      <BaseFormLabel for="input_user"> User </BaseFormLabel>
                      <VSelect
                        id="input_user"
                        v-model="formData.user"
                        :filterable="false"
                        label="fullName"
                        :options="userOptions"
                        :reduce="(user) => user.id"
                        @search="onSearchUsers"
                        :disabled="formProps.isPending"
                        class="vue-select"
                      >
                        <template #option="{ email, fullName }">
                          <p>{{ email }}</p>
                          <span class="text-sm text-gray-600">{{ fullName }}</span>
                        </template>
                      </VSelect>
                      <BaseFormInlineMessage v-if="errors.length" danger>
                        {{ errors[0] }}
                      </BaseFormInlineMessage>
                    </BaseFormGroup>
                  </template>
                </ValidationProvider>
                <template v-else>
                  <ValidationProvider name="email" slim vid="email" rules="required">
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel for="input_email"> Email </BaseFormLabel>
                        <BaseFormInput
                          type="email"
                          id="input_email"
                          v-model="formData.email"
                          :disabled="formProps.isPending"
                        />
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                  <ValidationProvider name="firstName" rules="required" slim vid="firstName">
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel for="input_first_name"> First Name </BaseFormLabel>
                        <BaseFormInput
                          type="text"
                          id="input_first_name"
                          v-model="formData.firstName"
                          :disabled="formProps.isPending"
                        />
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                  <ValidationProvider name="lastName" rules="required" slim vid="lastName">
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel for="input_last_name"> First Name </BaseFormLabel>
                        <BaseFormInput
                          type="text"
                          id="input_last_name"
                          v-model="formData.lastName"
                          :disabled="formProps.isPending"
                        />
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                  <ValidationProvider name="phone" slim vid="phone">
                    <template #default="{ errors }">
                      <BaseFormGroup>
                        <BaseFormLabel for="input_phone"> Phone </BaseFormLabel>
                        <BaseFormInput
                          type="text"
                          id="input_phone"
                          v-model="formData.phone"
                          :disabled="formProps.isPending"
                          class="w-1/2"
                        />
                        <BaseFormInlineMessage v-if="errors.length" danger>
                          {{ errors[0] }}
                        </BaseFormInlineMessage>
                      </BaseFormGroup>
                    </template>
                  </ValidationProvider>
                </template>
                <ValidationProvider v-if="$isAdminOrSuperAdmin" name="role" slim vid="roleId">
                  <template #default="{ errors }">
                    <BaseFormGroup>
                      <BaseFormLabel for="input_role"> Role </BaseFormLabel>
                      <div>
                        <BaseFormSelect
                          id="input_role"
                          v-model="formData.roleId"
                          :options="roles"
                          :disabled="formProps.isPending"
                        />
                      </div>
                      <BaseFormInlineMessage v-if="errors.length" danger>
                        {{ errors[0] }}
                      </BaseFormInlineMessage>
                    </BaseFormGroup>
                  </template>
                </ValidationProvider>
              </div>
              <div class="flex space-x-2">
                <BaseButton primary :disabled="formProps.invalid || formProps.isPending">
                  Save
                </BaseButton>
                <BaseButton
                  type="button"
                  primary
                  outline
                  :disabled="formProps.isPending"
                  @click="close"
                >
                  Cancel
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VModal>
  </BaseVeeForm>
</template>

<script>
import { debounce, pick } from 'lodash-es';
import usersClient from '@/api/clients/usersClient';

export default {
  name: 'AccountMemberAddModal',
  props: {
    action: {
      type: Function,
      required: true,
    },
    roles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      formData: {
        user: null,
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        roleId: '',
      },
      addFromExistingUser: false,
      userOptions: [],
      roleOptions: [],
    };
  },
  created() {
    this.loadData();
  },
  methods: {
    setUserOptions(users) {
      this.userOptions = users;
    },
    onSearchUsers(search, loading) {
      if (search.length) {
        loading(true);
        this.searchUsers(loading, search, this);
      }
    },
    searchUsers: debounce(
      (loading, search, vm) =>
        usersClient
          .getUsers({ filter: { search } })
          .then((response) => {
            vm.setUserOptions(response.data.data);
          })
          .finally(() => {
            loading(false);
          }),
      350
    ),
    close() {
      this.$refs.modal.close();
    },
    async loadData() {
      try {
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setRoleOptions(roles) {
      this.roleOptions = roles.map((role) => ({
        value: role.id,
        label: role.name,
      }));
    },
    async submit() {
      try {
        await this.action(
          pick(
            this.formData,
            this.addFromExistingUser
              ? ['user', 'roleId']
              : ['email', 'firstName', 'lastName', 'phone', 'roleId']
          )
        );

        this.close();

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
